import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import API_BASE_URL, { toAppPath } from "./config";
import "./Login.css";


const Login = () => {
  const { login, logout, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // If user is already logged in as org admin, redirect to panel
  // If logged in as super admin, log them out to ensure separation
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'org_admin') {
        navigate("/admin", { replace: true });
      } else if (user.role === 'super_admin') {
        // User is logged in as super_admin but trying to access org admin login
        // Log them out to ensure complete separation
        logout();
      }
    }
  }, [isAuthenticated, user, navigate, logout]);
  const [credentials, setCredentials] = useState({
    email: "",
    password: ""
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Check for success message from signup
  const successMessage = location.state?.message;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    setError(""); // Clear error when user starts typing
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!credentials.email) {
      setError("Please enter your email address first");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: credentials.email }),
      });

      const data = await response.json();

      if (response.ok) {
        navigate("/reset-password", { state: { email: credentials.email }, replace: true });
      } else {
        setError(data.message || data.error || "Failed to send reset link");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        }),
      });

      const data = await response.json();

      if (response.ok && data.token) {
        login(data.token, data.user);
        // Full navigation so admin loads with token from localStorage (avoids auth race)
        window.location.replace(toAppPath("/admin"));
        return;
      } else {
        setError(data.message || data.error || "Invalid email or password");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="auth-illustration">
        <div className="brand">
          <img className="brand-logo-img" src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`} alt="WeTano Solutions" />
          <div className="brand-text">
            <h2>WeTano Solutions</h2>
            <p>Organization Admin Console</p>
          </div>
        </div>
        <ul className="highlights">
          <li>Enhance customer support experience</li>
          <li>Monitor and manage conversations in real-time</li>
          <li>Build and organize your knowledge base effortlessly</li>
        </ul>
      </div>

      <div className="login-card">
        <div className="brand-mini">
          <img src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`} alt="WeTano Solutions" />
          <span>WeTano Solutions</span>
        </div>
        <div className="login-header">
          <h1>Welcome back</h1>
          <p>Organization Admin Login</p>
          <span className="sub-text">
            Sign in with your organization email
          </span>
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              placeholder="name@company.com"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label htmlFor="password">Password</label>
              <button
                type="button"
                onClick={handleForgotPassword}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '12px',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                Forgot password?
              </button>
            </div>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
              disabled={isLoading}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account? <Link to="/signup">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
